import React, { useEffect, useState } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import dayjs from 'dayjs';
import {
  DatePicker,
  Table,
  Tag,
  Avatar,
  Modal,
  Upload,
  Button,
  Popconfirm,
  Popover
} from 'antd';
import { faClipboardCheck, faEye, faFileExcel, faPencil, faPlus, faTrash } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'antd/es/typography/Link';
import { userAtom, notificationsAtom, userPrivilagesAtom } from '../../../atoms/Atoms';
import MainPage from '../../shared-components/main-page/MainPage';
import { H1, H4 } from '../../shared-components/typography/Title';
import api from '../../../api/api';
import {
  toCurrency,
  thousand,
  getDatePresets,
  getReceivingReportColumns,
  cleanReceivingReportDataForExport,
  getReceivingReportExportFields
} from '../../../functions';
import Select from '../../shared-components/form/Select';
import CustomButton from '../../shared-components/buttons/Button';
import Form from '../../shared-components/form/Form';
import DateSelector from '../../shared-components/form/DatePicker';
import Input from '../../shared-components/form/Input';

function ReceivingReport() {
  const { t, i18n } = useTranslation();
  const { RangePicker } = DatePicker;
  const user = useRecoilValue(userAtom);
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [dateRange, setDateRange] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [reportData, setReportData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastSearch, setLastSearch] = useState('');

  function getData(selectedPage, sTerm) {
    if (!dateRange.start || !dateRange.end) {
      return;
    }
    setLoading(true);
    setLastSearch(sTerm);
    setCurrentPage(selectedPage);
    const startDate = dayjs(dateRange.start);
    const endDate = dayjs(dateRange.end);
    const utcOffset = startDate.utcOffset();
    const startString = startDate.add(-1 * utcOffset, 'minute').format('YYYY-MM-DDThh:mm:ss');
    const endString = endDate.endOf('day').add(-1, 'second').add(-1 * utcOffset, 'minute').format('YYYY-MM-DDThh:mm:ss');
    api.post('reports/receiving', {
        StartDate: startString,
        EndDate: endString,
        Page: currentPage,
        Size: pageSize,
        ForExport: false,
        Query: sTerm,
      })
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setReportData(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  function exportData() {
    if (!dateRange.start || !dateRange.end) {
      return;
    }
    setLoading(true);
    const startDate = dayjs(dateRange.start);
    const endDate = dayjs(dateRange.end);
    const utcOffset = startDate.utcOffset();
    const startString = startDate.add(-1 * utcOffset, 'minute').format('YYYY-MM-DDThh:mm:ss');
    const endString = endDate.endOf('day').add(-1, 'second').add(-1 * utcOffset, 'minute').format('YYYY-MM-DDThh:mm:ss');
    api.post('reports/receiving/excel', {
        StartDate: startString,
        EndDate: endString,
        ForExport: true,
        Query: lastSearch,
      }, {
        responseType: 'blob'
      })
      .then((response) => {
        setLoading(false);
        const fileName = `${t('receiving_report')} ${startDate.add(-1 * utcOffset, 'minute').format('MM DD, YYYYY')}-${endDate.add(-1 * utcOffset, 'minute').format('MM DD, YYYYY')}.xlsx`;
        const href = URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  function isRangeMoreThanMonth() {
    try {
      const startDate = dayjs(dateRange?.start);
      const endDate = dayjs(dateRange?.end);
      const monthDiff = endDate.diff(startDate, 'month', true);
      return monthDiff > 1;
    } catch (err) {
      return false;
    }
  }

  useEffect(() => {
    setDateRange({
      start: dayjs().startOf('week').format('MMM DD, YYYY'),
      end: dayjs().endOf('day').format('MMM DD, YYYY')
    });
  }, []);

  useEffect(() => {
    getData(0, lastSearch);
  }, [pageSize, dateRange]); // add filters sorts here

  return (
    <MainPage>
      <H1 text={t('receiving_report')} />
      <div className="space-y-2">
        <div className="w-full mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-2">
          {/* <Select
            className="col-span-1 mt-2"
            disabled={loading}
            label={t('type')}
            id="typefilter"
            name="typefilter"
            value={incidentalTypeFilter}
            onChange={(obj) => setIncidentalTypeFilter(obj.target.value)}
            options={types}
          />
          <Select
            className="col-span-1 mt-2"
            disabled={loading}
            label={t('approval_status')}
            id="approvalfilter"
            name="approvalfilter"
            value={incidentalApprovalFilter}
            onChange={(obj) => setIncidentalApprovalFilter(obj.target.value)}
            options={statusFilters}
          />
          <Select
            className="col-span-1 mt-2"
            disabled={loading}
            label={t('payment_status')}
            id="paymentfilter"
            name="paymentfilter"
            value={incidentalPaymentFilter}
            onChange={(obj) => setIncidentalPaymentFilter(obj.target.value)}
            options={statusFilters}
          /> */}
          <div className="col-span-1 md:col-span-2 lg:col-start-4 lg:col-span-1 xl:col-start-5">
            <RangePicker
              disabled={loading}
              className="w-full h-11 mt-2 bg-transparent"
              disabledDate={(current) => current && current.valueOf() > Date.now()}
              presets={getDatePresets(t)}
              value={[
                dayjs(dateRange?.start),
                dayjs(dateRange?.end)
              ]}
              onChange={(obj) => {
                setDateRange({
                  start: obj[0],
                  end: obj[1]
                });
              }}
              format="MMM DD, YYYY"
            />
          </div>
        </div>
        <div className="flex space-x-2">
          <Input
            label={t('search')}
            type="text"
            name="search"
            key="search"
            value={searchTerm}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                getData(0, searchTerm)
              }
            }}
            onChange={(obj) => setSearchTerm(obj.target.value)}
          />
          <CustomButton
            className="bg-primaryColor px-4"
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="border-primaryBorderColor"
            loading={loading}
            text={t('search')}
            onClick={() => getData(0, searchTerm)}
          />
        </div>
        <Table
          bordered
          locale={{
            emptyText: t('no_data')
          }}
          title={() => (
            <div className="flex">
              <div className="space-x-2">
                <strong>{t('total')}:</strong>
                {reportData?.report && (
                  <span>{thousand(reportData?.total)}</span>
                )}
              </div>
              <div className="flex items-center space-x-2 ml-auto">
                {isRangeMoreThanMonth() && (
                  <Popover trigger="hover" content={t('max_of_one_month_for_export')}>
                    <div>
                      <CustomButton
                        className="bg-green-600 px-4"
                        textColor="text-primaryTextColor"
                        loaderColor="bg-primaryTextColor"
                        borderColor="bg-green-600"
                        loading={loading}
                        disabled
                        icon={faFileExcel}
                        text={t('export')}
                        onClick={() => exportData()}
                      />
                    </div>
                  </Popover>
                )}
                {!isRangeMoreThanMonth() && (
                  <CustomButton
                    className="bg-green-600 px-4"
                    textColor="text-primaryTextColor"
                    loaderColor="bg-primaryTextColor"
                    borderColor="bg-green-600"
                    loading={loading}
                    icon={faFileExcel}
                    text={t('export')}
                    onClick={() => exportData()}
                  />
                )}
                <strong>{t('last_updated_on')}:</strong>
                {reportData?.lastUpdatedAt && (
                  <span>{dayjs(reportData?.lastUpdatedAt).add(-4, 'hour').format('MMM DD, YYYY hh:mm A')}</span>
                )}
              </div>
            </div>
          )}
          rowKey="id"
          loading={loading}
          className="mt-2"
          pagination={{
            pageSize,
            showSizeChanger: true,
            current: currentPage + 1,
            total: reportData?.total,
            onChange: (page, size) => {
              if (page - 1 !== currentPage) {
                setCurrentPage(page - 1);
                getData(page - 1);
              } else {
                setPageSize(size);
              }
            }
          }}
          dataSource={reportData?.report}
          columns={getReceivingReportColumns(t)}
        />
      </div>
    </MainPage>
  );
}

export default ReceivingReport;
